import { z } from 'zod'

/**
 * List-endpoint parameters, shared so that no endpoint invents its own.
 *
 * `docs/ARCHITECTURE.md` §4: "List endpoints have **mandatory pagination caps** enforced
 * server-side." Mandatory is the word that matters — the cap is in the schema every list
 * route parses its query with, so an endpoint cannot ship without one, and the published
 * OpenAPI document states the maximum rather than leaving a client to discover it.
 *
 * The values arrive as query strings, so they are coerced. A missing parameter takes the
 * default; a present but unparseable one is a validation error rather than a silent
 * fallback, because a client asking for `limit=all` should be told no, not handed 25 rows
 * that look like the whole set.
 */

/** The most rows any list endpoint will return in one page. */
export const MAX_PAGE_SIZE = 100

/** The page size a client gets when it does not ask for one. */
export const DEFAULT_PAGE_SIZE = 25

export const paginationSchema = z.object({
  offset: z.coerce
    .number()
    .int()
    .min(0)
    .default(0)
    .meta({ description: 'Rows to skip. Defaults to 0.' }),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE)
    .meta({
      description: `Rows to return, at most ${MAX_PAGE_SIZE}. Defaults to ${DEFAULT_PAGE_SIZE}.`,
    }),
})

export type Pagination = z.infer<typeof paginationSchema>

/** Sort direction for list endpoints that expose an ordering. */
export const sortDirectionSchema = z
  .enum(['asc', 'desc'])
  .meta({ id: 'SortDirection', description: 'Ascending or descending order.' })

export type SortDirection = z.infer<typeof sortDirectionSchema>
